import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { productService, categoryService } from '../services/api';
import ProductCard from '../components/ProductCard';
import { FaChair, FaTable, FaArchive, FaTools, FaLaptop, FaRocket, FaTruckMoving } from 'react-icons/fa';
import { Container, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/effect-coverflow';
import 'swiper/css/pagination';
import { Autoplay, EffectCoverflow, Pagination } from 'swiper/modules';

const images = [
  '/images/Slide-one.webp',
  '/images/Slide-two.webp',
  '/images/Slide-three.webp',
];

const serviceImages = [
  {
    label: 'Office Furniture',
    img: '/images/ofc-furniture.jpg',
  },
  {
    label: 'Tech & Electronics',
    img: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=600&q=80',
  },
  {
    label: 'Startup Office Kits',
    img: '/images/ofc-kit.jpeg',
  },
  {
    label: 'Cargo Services',
    img: '/images/cargo-service.jpg',
  },
];

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    console.log('HomePage: Starting data fetch');
    const fetchData = async () => {
      try {
        console.log('HomePage: Fetching data from API');
        const [productsRes, categoriesRes] = await Promise.all([
          productService.getFeaturedProducts(),
          categoryService.getAllCategories()
        ]);
        
        console.log('HomePage: Products data received:', productsRes.data);
        console.log('HomePage: Categories data received:', categoriesRes.data);
        
        setFeaturedProducts(productsRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Error fetching home data:', error);
        setError('Failed to load content. Please try again later.');
      } finally {
        setLoading(false);
        console.log('HomePage: Data fetch complete');
      }
    };

    fetchData();
  }, []);

  // Get category icon based on name
  const getCategoryIcon = (name) => {
    switch (name?.toLowerCase()) {
      case 'chairs':
        return <FaChair size={36} />;
      case 'desks':
        return <FaTable size={36} />;
      case 'storage':
        return <FaArchive size={36} />;
      case 'accessories':
        return <FaTools size={36} />;
      default:
        return <FaChair size={36} />;
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <Swiper
       effect="coverflow"
       grabCursor
       centeredSlides
       slidesPerView="auto"
       coverflowEffect={{
         rotate: 0,
         stretch: 0,
         depth: 100,
         modifier: 2.5,
       }}
       loop
       autoplay={{
         delay: 2000,
         disableOnInteraction: false,
       }}
        pagination={true} // 🔘 dots here
        modules={[Autoplay, EffectCoverflow, Pagination]}
        className="mySwiper"
      >
        {images.map((src, index) => (
          <SwiperSlide key={index}>
              <img src={src} alt={`Slide ${index}`} style={{ height: '656px', width: '100%' }} />
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Categories Section */}
      <section className="py-5">
        <Container>
          <h2 className="text-center mb-4">Shop by Category</h2>
          <Row>
            {categories.map((category) => (
              <Col key={category.id} md={3} sm={6} className="mb-4">
                <Link to={`/categories/${category.id}`} className="text-decoration-none">
                  <div className="card h-100 text-center p-4 hover-shadow">
                    <div className="mb-3 text-primary">
                      {getCategoryIcon(category.name)}
                    </div>
                    <h3 className="h5 mb-2">{category.name}</h3>
                    <p className="text-muted small">{category.description}</p>
                  </div>
                </Link>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section style={{ background: 'linear-gradient(120deg, #4f46e5 0%, #06b6d4 100%)', padding: '3.5rem 0 3rem 0' }}>
        <Container>
          <h1 className="fw-bold text-center mb-2" style={{ fontSize: '2.2rem', color: 'white', letterSpacing: 1 }}>
            Empowering Business with Ready-to-Use-Office Essentials
          </h1>
          <p className="text-center mb-5" style={{ maxWidth: 700, margin: '0 auto', color: '#e0e7ff', fontSize: 18 }}>
            Welcome to Telering Process Pvt. Ltd., your all-in-one destination for premium-quality office setup essentials—perfect for startups, small businesses, freelancers, and corporate offices alike.
          </p>
          <Row className="justify-content-center mb-4" style={{ gap: 0 }}>
            {serviceImages.map((service, idx) => (
              <Col xs={12} md={6} lg={3} className="mb-4 d-flex justify-content-center" key={service.label}>
                <div style={{
                  background: 'white',
                  borderRadius: 24,
                  boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
                  padding: 0,
                  textAlign: 'center',
                  border: '2px solid #e0e7ff',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  overflow: 'hidden',
                  transition: 'transform 0.2s',
                  position: 'relative',
                  width: '100%'
                }}>
                  <div style={{ position: 'relative', width: '100%', height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img src={service.img} alt={service.label} style={{ width: '100%', height: 180, objectFit: 'cover', borderTopLeftRadius: 24, borderTopRightRadius: 24 }} />
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: 'rgba(0,0,0,0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <span style={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: 20,
                        letterSpacing: 1,
                        textShadow: '0 2px 8px rgba(0,0,0,0.25)',
                        padding: '0 10px',
                        textAlign: 'center',
                      }}>{service.label}</span>
                    </div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
          <div className="text-center mt-2">
            <Link to="/products" style={{
              display: 'inline-block',
              background: '#ffffff',
              color: '#000000',
              borderRadius: 32,
              padding: '14px 48px',
              fontWeight: 600,
              fontSize: 22,
              letterSpacing: 1,
              boxShadow: '0 2px 8px 0 rgba(31, 38, 135, 0.10)',
              textDecoration: 'none',
              border: 'none',
              transition: 'background 0.2s',
            }}>Shop Now</Link>
          </div>
        </Container>
      </section>

      

      {/* Featured Products */}
      <section className="py-5 bg-light">
        <Container>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Featured Products</h2>
            <Link to="/products" className="text-decoration-none">
              View All
            </Link>
          </div>
          <Row>
            {featuredProducts.map((product) => (
              <Col key={product.id} lg={3} md={4} sm={6} className="mb-4">
                <ProductCard product={product} />
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="py-5" style={{ backgroundColor: 'var(--primary-color)', color: 'white' }}>
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="mb-4 mb-md-0">
              <h1 className="display-4 fw-bold mb-3">
                Create Your Perfect Workspace
              </h1>
              <p className="lead mb-4">
                Discover premium office furniture and accessories designed for 
                comfort, productivity, and style.
              </p>
              <Link to="/products" className="btn btn-light btn-lg">
                Shop Now
              </Link>
            </Col>
            <Col md={6} className="text-center">
              <img 
                src="https://images.unsplash.com/photo-1524758631624-e2822e304c36?ixlib=rb-1.2.1&auto=format&fit=crop&w=1050&q=80" 
                alt="Modern office setup" 
                className="img-fluid rounded"
                style={{ maxHeight: '400px', objectFit: 'cover' }}
              />
            </Col>
          </Row>
        </Container>
      </section>
      

      {/* Benefits Section */}
      <section className="py-5">
        <Container>
          <h2 className="text-center mb-4">Why Choose Us</h2>
          <Row>
            <Col md={4} className="mb-4 mb-md-0">
              <div className="card h-100 text-center p-4">
                <div className="mb-3" style={{ fontSize: '3rem', color: 'var(--primary-color)' }}>🚚</div>
                <h3 className="h4 mb-2">Free Shipping</h3>
                <p className="mb-0">On all orders over ₹5000</p>
              </div>
            </Col>
            <Col md={4} className="mb-4 mb-md-0">
              <div className="card h-100 text-center p-4">
                <div className="mb-3" style={{ fontSize: '3rem', color: 'var(--primary-color)' }}>⚡</div>
                <h3 className="h4 mb-2">Easy Assembly</h3>
                <p className="mb-0">Simple setup with included tools</p>
              </div>
            </Col>
            <Col md={4}>
              <div className="card h-100 text-center p-4">
                <div className="mb-3" style={{ fontSize: '3rem', color: 'var(--primary-color)' }}>✅</div>
                <h3 className="h4 mb-2">Quality Guarantee</h3>
                <p className="mb-0">2-year warranty on all products</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Newsletter */}
      <section className="py-5 text-white" style={{ backgroundColor: 'var(--secondary-color)' }}>
        <Container className="text-center">
          <h2 className="mb-3">Join Our Newsletter</h2>
          <p className="mb-4">Get exclusive offers and updates on new products</p>
          <Row className="justify-content-center">
            <Col md={6}>
              <div className="input-group">
                <input 
                  type="email" 
                  className="form-control" 
                  placeholder="Enter your email"
                />
                <button className="btn btn-primary">
                  Subscribe
                </button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default HomePage; 